import axios from 'axios';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

export interface FormAttachment {
  id: number;
  formId: string;
  identifier: string;
  filename: string;
  contentType: string;
  url: string;
  path: string;
  taskIndex: number | null;
  imageMetadata: {
    orientation?: string;
    width?: number;
    height?: number;
  } | null;
  createdAt: string;
  updatedAt: string;
}

export interface FormAttachmentsByTask {
  [taskKey: string]: FormAttachment[];
}

class FormAttachmentService {
  /**
   * Get all attachments for a form
   */
  async getFormAttachments(formId: string): Promise<FormAttachment[]> {
    try {
      const url = `${BASE_URL}/forms/${formId}/attachments`;
      console.log('FormAttachmentService: Fetching attachments from:', url);
      const response = await axios.get(url);
      console.log('FormAttachmentService: Response:', response.data);
      return response.data;
    } catch (error) {
      console.error('FormAttachmentService: Error fetching form attachments:', error);
      throw error;
    }
  }

  /**
   * Get form attachments grouped by task
   */
  async getFormAttachmentsByTask(formId: string): Promise<FormAttachmentsByTask> {
    try {
      const response = await axios.get(`${BASE_URL}/forms/${formId}/attachments/by-task`);
      return response.data;
    } catch (error) {
      console.error('Error fetching form attachments by task:', error);
      throw error;
    }
  }

  /**
   * Get attachments for a specific task
   */
  async getTaskAttachments(formId: string, taskIndex: number): Promise<FormAttachment[]> {
    try {
      const allAttachments = await this.getFormAttachmentsByTask(formId);
      const taskKey = `task-${taskIndex}`;
      return allAttachments[taskKey] || [];
    } catch (error) {
      console.error('Error fetching task attachments:', error);
      throw error;
    }
  }

  /**
   * Get general attachments (not associated with a specific task)
   */
  async getGeneralAttachments(formId: string): Promise<FormAttachment[]> {
    try {
      const allAttachments = await this.getFormAttachmentsByTask(formId);
      return allAttachments['general'] || [];
    } catch (error) {
      console.error('Error fetching general attachments:', error);
      throw error;
    }
  }

  /**
   * Get the count of attachments for a form (efficient check for photo indicator)
   */
  async getFormAttachmentCount(formId: string): Promise<number> {
    try {
      const attachments = await this.getFormAttachments(formId);
      return attachments.length;
    } catch (error) {
      console.error('Error fetching form attachment count:', error);
      return 0; // Return 0 on error to avoid breaking the UI
    }
  }

  /**
   * Get aggregated Daily Log attachments for a given task
   */
  async getAttachmentsForTask(taskId: number): Promise<FormAttachment[]> {
    const response = await axios.get(`/api/forms/task/${taskId}/attachments`);
    return response.data;
  }
}

export default new FormAttachmentService();
